/**
 * StructuringPanel — Steuer-Strukturierungs-Engine + Lawyer-Match.
 *
 * Drei sichtbare Phasen:
 *   1. Profil-Form    — User füllt Wohnsitz / Vermögen / Familie / Ziele.
 *   2. Optionen-Liste — 3-5 Strukturen mit Score-Bars + Pro/Contra.
 *   3. Lawyer-Match   — passende Berater + Anfrage-Form (DSGVO-Konsent).
 *
 * Compliance-Linie:
 *   • Klare "VERGLEICH, KEINE EMPFEHLUNG" Botschaft im Header.
 *   • Disclaimer-Block IMMER sichtbar oberhalb der Optionen.
 *   • Lawyer-Match nur mit explizitem GDPR-Consent (Checkbox).
 */
import { useState } from "react";
import {
  analyzeStructuring,
  submitStructuringInquiry,
  type AdvisorSummary,
  type StructuringAnalysisResult,
  type StructuringOption,
  type StructuringProfile,
} from "../lib/api";

const ASSET_OPTIONS = [
  "Cash / Tagesgeld",
  "Aktien & Fonds",
  "Immobilien (selbstgenutzt)",
  "Immobilien (vermietet)",
  "Unternehmensbeteiligung",
  "Crypto",
  "Kunst / Sammler",
  "Lebensversicherungen",
  "Edelmetalle",
];

const GOAL_OPTIONS = [
  "Steuerlast minimieren",
  "Asset Protection / Gläubigerschutz",
  "Erbfolge regeln",
  "Anonymität / Privatsphäre",
  "Standort-Diversifikation",
  "Wegzug / Auswanderung",
  "Vorbereitung Unternehmens-Verkauf",
  "Crypto rechtssicher strukturieren",
];

const DIFFICULT_OPTIONS = [
  "US-Person / FATCA-Pflicht",
  "Pre-Immigration in die USA",
  "Erbfall mit Auslandsvermögen",
  "Trennung / Scheidung mit Vermögen",
  "Insolvenz-Risiko (Asset Protection)",
  "Streit mit Finanzamt / Betriebsprüfung",
  "Crypto-Compliance-Lücke",
  "Hochsteuer-Wohnsitz, Wegzugswunsch",
  "Hinzurechnungsbesteuerung droht (§§ 7-14 AStG)",
  "Wegzugsbesteuerung droht (§ 6 AStG)",
];

export default function StructuringPanel() {
  // --- Phase 1: Profil ---
  const [currentResidence, setCurrentResidence] = useState("Deutschland");
  const [targetResidence, setTargetResidence] = useState("");
  const [citizenships, setCitizenships] = useState("Deutsch");
  const [netWorthEur, setNetWorthEur] = useState<string>("1000000");
  const [assetClasses, setAssetClasses] = useState<string[]>(["Cash / Tagesgeld", "Aktien & Fonds"]);
  const [familySituation, setFamilySituation] = useState(
    "Verheiratet, zwei minderjährige Kinder. Ehegattenwohnsitz identisch."
  );
  const [goals, setGoals] = useState<string[]>(["Steuerlast minimieren", "Erbfolge regeln"]);
  const [timeHorizon, setTimeHorizon] = useState("Mittelfristig (3-5 Jahre)");
  const [difficultSituations, setDifficultSituations] = useState<string[]>([]);
  const [additionalContext, setAdditionalContext] = useState("");

  // --- Phase 2: Analyse ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<StructuringAnalysisResult | null>(null);
  const [advisors, setAdvisors] = useState<AdvisorSummary[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());

  const toggleSet = (set: string[], setSet: (v: string[]) => void, val: string) => {
    setSet(set.includes(val) ? set.filter((x) => x !== val) : [...set, val]);
  };

  const runAnalysis = async () => {
    setLoading(true);
    setError(null);
    setAnalysis(null);
    setAdvisors([]);
    try {
      const profile: StructuringProfile = {
        currentResidence: currentResidence.trim(),
        targetResidence: targetResidence.trim() || undefined,
        citizenships: citizenships.split(",").map((s) => s.trim()).filter(Boolean),
        netWorthEur: Number(netWorthEur) || 0,
        assetClasses,
        familySituation: familySituation.trim(),
        goals,
        timeHorizon: timeHorizon.trim(),
        difficultSituations: difficultSituations.length ? difficultSituations : undefined,
        additionalContext: additionalContext.trim() || undefined,
      };
      const r = await analyzeStructuring(profile);
      setAnalysis(r.analysis);
      setAdvisors(r.suggestedAdvisors);
      // automatisch Top-Option preselecten
      if (r.analysis.options.length > 0) {
        setSelectedOptions(new Set([r.analysis.options[0]!.id]));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <ComplianceBanner />

      <ProfileForm
        state={{
          currentResidence,
          setCurrentResidence,
          targetResidence,
          setTargetResidence,
          citizenships,
          setCitizenships,
          netWorthEur,
          setNetWorthEur,
          assetClasses,
          familySituation,
          setFamilySituation,
          goals,
          timeHorizon,
          setTimeHorizon,
          difficultSituations,
          additionalContext,
          setAdditionalContext,
        }}
        toggleAsset={(v) => toggleSet(assetClasses, setAssetClasses, v)}
        toggleGoal={(v) => toggleSet(goals, setGoals, v)}
        toggleDifficult={(v) =>
          toggleSet(difficultSituations, setDifficultSituations, v)
        }
        loading={loading}
        onRun={runAnalysis}
      />

      {error && (
        <div className="rounded-lg bg-red-950/50 border border-red-800 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      {analysis && (
        <AnalysisResultBlock
          analysis={analysis}
          advisors={advisors}
          selectedOptions={selectedOptions}
          onToggleOption={(id) => {
            const next = new Set(selectedOptions);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            setSelectedOptions(next);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
//   Compliance-Banner — IMMER sichtbar
// ---------------------------------------------------------------------------

function ComplianceBanner() {
  return (
    <div className="rounded-2xl border border-amber-700/60 bg-amber-950/30 px-5 py-4 text-sm text-amber-100/95 leading-relaxed">
      <p className="font-semibold uppercase tracking-wider text-amber-300 text-xs mb-1">
        Wichtige Klarstellung
      </p>
      <p>
        Diese Analyse <strong>vergleicht weltweit verfügbare Strukturen</strong> für Dein
        Profil — sie ist ausdrücklich <strong>KEINE Steuerberatung im Sinne § 1 StBerG</strong>{" "}
        und <strong>KEINE Empfehlung</strong> zur Umsetzung. Vor jeder Entscheidung muss ein{" "}
        <strong>zugelassener Steuerberater</strong> (im Wohnsitz-Staat) und je nach Sitzstaat
        ein dortiger <strong>Anwalt</strong> eingeschaltet werden. Wir zeigen Dir am Ende
        passende Kontakte.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
//   ProfileForm
// ---------------------------------------------------------------------------

interface ProfileFormProps {
  state: {
    currentResidence: string;
    setCurrentResidence: (v: string) => void;
    targetResidence: string;
    setTargetResidence: (v: string) => void;
    citizenships: string;
    setCitizenships: (v: string) => void;
    netWorthEur: string;
    setNetWorthEur: (v: string) => void;
    assetClasses: string[];
    familySituation: string;
    setFamilySituation: (v: string) => void;
    goals: string[];
    timeHorizon: string;
    setTimeHorizon: (v: string) => void;
    difficultSituations: string[];
    additionalContext: string;
    setAdditionalContext: (v: string) => void;
  };
  toggleAsset: (v: string) => void;
  toggleGoal: (v: string) => void;
  toggleDifficult: (v: string) => void;
  loading: boolean;
  onRun: () => void;
}

function ProfileForm({
  state,
  toggleAsset,
  toggleGoal,
  toggleDifficult,
  loading,
  onRun,
}: ProfileFormProps) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field
          label="Aktueller steuerlicher Wohnsitz *"
          value={state.currentResidence}
          onChange={state.setCurrentResidence}
          placeholder="z.B. Deutschland"
        />
        <Field
          label="Erwogener Zielwohnsitz (optional)"
          value={state.targetResidence}
          onChange={state.setTargetResidence}
          placeholder="z.B. UAE, Schweiz, Portugal"
        />
        <Field
          label="Staatsangehörigkeiten (Komma-getrennt) *"
          value={state.citizenships}
          onChange={state.setCitizenships}
          placeholder="z.B. Deutsch, Italienisch"
        />
        <Field
          label="Vermögen (Größenordnung in EUR) *"
          value={state.netWorthEur}
          onChange={state.setNetWorthEur}
          placeholder="z.B. 1500000"
          inputType="number"
        />
        <Field
          label="Zeit-Horizont *"
          value={state.timeHorizon}
          onChange={state.setTimeHorizon}
          placeholder="z.B. Mittelfristig (3-5 Jahre)"
        />
      </div>

      <ChipGroup
        label="Vermögensarten"
        options={ASSET_OPTIONS}
        selected={state.assetClasses}
        onToggle={toggleAsset}
      />

      <ChipGroup
        label="Hauptziele"
        options={GOAL_OPTIONS}
        selected={state.goals}
        onToggle={toggleGoal}
      />

      <ChipGroup
        label="Schwierige Lebenslage (optional, multi)"
        options={DIFFICULT_OPTIONS}
        selected={state.difficultSituations}
        onToggle={toggleDifficult}
        accent="amber"
      />

      <div>
        <label className="block text-sm text-slate-300 mb-1">
          Familien-Situation *
        </label>
        <textarea
          value={state.familySituation}
          onChange={(e) => state.setFamilySituation(e.target.value)}
          rows={2}
          className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-court-500"
        />
      </div>

      <div>
        <label className="block text-sm text-slate-300 mb-1">
          Weiterer Kontext (optional)
        </label>
        <textarea
          value={state.additionalContext}
          onChange={(e) => state.setAdditionalContext(e.target.value)}
          rows={3}
          placeholder="z.B. konkrete Konstruktion die Du erwägst, laufende Gerichtsverfahren, anstehende Änderungen…"
          className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-court-500"
        />
      </div>

      <button
        onClick={onRun}
        disabled={loading}
        className="w-full rounded-lg bg-gradient-to-r from-court-500 to-court-700 hover:from-court-400 hover:to-court-600 disabled:opacity-50 px-4 py-3 font-semibold transition shadow-lg shadow-court-700/30"
      >
        {loading ? "Analyse läuft…" : "🌍 Strukturen weltweit vergleichen"}
      </button>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  inputType?: "text" | "number";
}

function Field({ label, value, onChange, placeholder, inputType = "text" }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-sm text-slate-300 mb-1">{label}</span>
      <input
        type={inputType}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-court-500"
      />
    </label>
  );
}

function ChipGroup({
  label,
  options,
  selected,
  onToggle,
  accent = "court",
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
  accent?: "court" | "amber";
}) {
  const onClass =
    accent === "amber"
      ? "bg-amber-700/30 border-amber-500 text-amber-100"
      : "bg-court-700/30 border-court-500 text-court-100";
  return (
    <div>
      <label className="block text-sm text-slate-300 mb-1">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const isOn = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              className={`text-xs rounded-full border px-3 py-1 transition ${
                isOn
                  ? onClass
                  : "bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
//   Analysis Result Block — Optionen, Disclaimer, Berater
// ---------------------------------------------------------------------------

function AnalysisResultBlock({
  analysis,
  advisors,
  selectedOptions,
  onToggleOption,
}: {
  analysis: StructuringAnalysisResult;
  advisors: AdvisorSummary[];
  selectedOptions: Set<string>;
  onToggleOption: (id: string) => void;
}) {
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-court-700/60 bg-slate-900/70 p-5">
        <p className="text-xs uppercase tracking-wider text-slate-400">
          Gesamt-Einschätzung
        </p>
        <p className="mt-1 text-slate-100 text-base leading-relaxed">
          {analysis.overallAssessment}
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-base font-semibold text-court-300">
          🌍 Weltweit verfügbare Strukturen für Dein Profil ({analysis.options.length})
        </h3>
        {analysis.options.map((opt) => (
          <OptionCard
            key={opt.id}
            option={opt}
            selected={selectedOptions.has(opt.id)}
            onToggle={() => onToggleOption(opt.id)}
          />
        ))}
      </div>

      {analysis.criticalQuestionsForLawyer.length > 0 && (
        <div className="rounded-2xl border border-court-700/60 bg-slate-900/70 p-5">
          <h3 className="text-base font-semibold text-court-300 mb-2">
            ⚠ Diese Fragen MUSST Du mit dem Berater zwingend klären
          </h3>
          <ol className="space-y-2 list-decimal list-inside text-sm text-slate-200 leading-relaxed">
            {analysis.criticalQuestionsForLawyer.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ol>
        </div>
      )}

      <DisclaimerBlock items={analysis.disclaimers} />

      {advisors.length > 0 && (
        <AdvisorMatchBlock
          advisors={advisors}
          selectedOptionIds={[...selectedOptions]}
        />
      )}
    </div>
  );
}

function OptionCard({
  option,
  selected,
  onToggle,
}: {
  option: StructuringOption;
  selected: boolean;
  onToggle: () => void;
}) {
  const fitColor =
    option.fitScore >= 75
      ? "text-emerald-300"
      : option.fitScore >= 50
        ? "text-amber-300"
        : "text-slate-400";
  const riskColor =
    option.abuseRiskScore <= 3
      ? "text-emerald-300"
      : option.abuseRiskScore <= 6
        ? "text-amber-300"
        : "text-red-300";

  const fmtRange = (r: { min: number; max: number }) =>
    `${r.min.toLocaleString("de-DE")} – ${r.max.toLocaleString("de-DE")} EUR`;

  return (
    <div
      className={`rounded-2xl border p-5 transition ${
        selected
          ? "border-court-500 bg-court-950/30 shadow-lg shadow-court-900/30"
          : "border-slate-800 bg-slate-900/40"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wider text-slate-500">
            {option.jurisdiction} · {option.category}
          </p>
          <h4 className="text-lg font-semibold text-slate-50 mt-0.5">
            {option.name}
          </h4>
          <p className="text-sm text-slate-300 mt-1 leading-relaxed">
            {option.fitSummary}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={`text-2xl font-mono font-bold ${fitColor}`}>
            {option.fitScore}
            <span className="text-xs text-slate-500 ml-0.5">/100</span>
          </span>
          <span className="text-[10px] uppercase tracking-wider text-slate-500">
            Eignung für Dich
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-4 text-xs">
        <div className="rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">
            § 42 AO Risiko
          </p>
          <p className={`mt-1 font-mono font-semibold ${riskColor}`}>
            {option.abuseRiskScore}/10
          </p>
        </div>
        <div className="rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">
            Setup-Kosten
          </p>
          <p className="mt-1 font-mono text-slate-200">
            {fmtRange(option.setupCostEur)}
          </p>
        </div>
        <div className="rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">
            Laufend p.a.
          </p>
          <p className="mt-1 font-mono text-slate-200">
            {fmtRange(option.ongoingCostEurPerYear)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1">
            Pro für Dein Profil
          </p>
          <ul className="space-y-1 text-slate-200">
            {option.pros.map((p, i) => (
              <li key={i}>+ {p}</li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-red-400 mb-1">
            Contra
          </p>
          <ul className="space-y-1 text-slate-200">
            {option.cons.map((p, i) => (
              <li key={i}>− {p}</li>
            ))}
          </ul>
        </div>
      </div>

      <details className="mt-4 text-xs">
        <summary className="cursor-pointer text-slate-400 hover:text-slate-200">
          Steuer-Effekt · Compliance · Substanz anzeigen
        </summary>
        <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3 text-slate-300">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              Steuer-Effekt
            </p>
            <ul className="space-y-1">
              {option.taxImpact.map((t, i) => (
                <li key={i}>• {t}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              Compliance-Pflichten
            </p>
            <ul className="space-y-1">
              {option.compliance.map((t, i) => (
                <li key={i}>• {t}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              Substanz-Anforderungen
            </p>
            <ul className="space-y-1">
              {option.substance.map((t, i) => (
                <li key={i}>• {t}</li>
              ))}
            </ul>
          </div>
        </div>
      </details>

      <div className="mt-4 flex items-center justify-between">
        <p className="text-[11px] text-slate-500">
          {option.requiresLocalCounsel
            ? "Erfordert zwingend lokalen Anwalt im Sitzstaat."
            : "Lokaler Anwalt nicht zwingend, aber empfohlen."}
        </p>
        <button
          onClick={onToggle}
          className={`text-xs rounded-lg border px-3 py-1.5 transition ${
            selected
              ? "bg-court-700/40 border-court-500 text-court-100"
              : "bg-slate-950/60 border-slate-700 text-slate-300 hover:border-slate-600"
          }`}
        >
          {selected ? "✓ ausgewählt" : "+ mit Berater besprechen"}
        </button>
      </div>
    </div>
  );
}

function DisclaimerBlock({ items }: { items: string[] }) {
  return (
    <details className="rounded-2xl border border-amber-800/60 bg-amber-950/20 p-5 text-sm">
      <summary className="cursor-pointer font-semibold text-amber-200">
        Rechtliche Hinweise · {items.length} Punkte
      </summary>
      <ul className="mt-3 space-y-2 text-amber-100/90 leading-relaxed">
        {items.map((d, i) => (
          <li key={i}>• {d}</li>
        ))}
      </ul>
    </details>
  );
}

// ---------------------------------------------------------------------------
//   AdvisorMatchBlock — passende Berater + Anfrage-Form
// ---------------------------------------------------------------------------

function AdvisorMatchBlock({
  advisors,
  selectedOptionIds,
}: {
  advisors: AdvisorSummary[];
  selectedOptionIds: string[];
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [profileSummary, setProfileSummary] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<{
    advisor: { name: string; affiliation: string; location: string };
    nextSteps: string[];
  } | null>(null);

  const submit = async () => {
    if (!chosen || !consent || profileSummary.trim().length < 20) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const r = await submitStructuringInquiry({
        advisorId: chosen,
        profileSummary: profileSummary.trim(),
        selectedOptionIds,
        contactName: contactName.trim(),
        contactEmail: contactEmail.trim(),
        contactPhone: contactPhone.trim() || undefined,
        consentGdpr: true,
      });
      setSubmitted({ advisor: r.advisor, nextSteps: r.nextSteps });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-2xl border border-emerald-700/60 bg-emerald-950/30 p-6">
        <h3 className="text-base font-semibold text-emerald-200 mb-2">
          ✓ Anfrage angenommen
        </h3>
        <p className="text-sm text-slate-200 mb-3">
          Vermittlung an <strong>{submitted.advisor.name}</strong> ({submitted.advisor.affiliation}, {submitted.advisor.location}) angefordert.
        </p>
        <ul className="space-y-1 text-sm text-slate-300 list-disc list-inside">
          {submitted.nextSteps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-court-700/60 bg-slate-900/70 p-5 space-y-4">
      <div>
        <h3 className="text-base font-semibold text-court-300">
          🤝 Passende Berater zur Validierung
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Wähle einen Berater aus. YAVU vermittelt die Anfrage manuell binnen 1-2 Werktagen.
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {advisors.map((a) => {
          const isOn = chosen === a.id;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => setChosen(a.id)}
              className={`text-left rounded-xl border p-4 transition ${
                isOn
                  ? "border-court-500 bg-court-950/40"
                  : "border-slate-800 bg-slate-950/40 hover:border-slate-700"
              }`}
            >
              <p className="font-semibold text-slate-100">{a.name}</p>
              <p className="text-xs text-slate-400">
                {a.title} · {a.affiliation}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                📍 {a.location} · 🗣️ {a.languages.join(", ")}
              </p>
              <p className="text-xs text-slate-300 mt-2 line-clamp-3">{a.bio}</p>
              <p className="text-[11px] text-slate-500 mt-2">
                Spezialgebiete: {a.specialties.join(" · ")}
              </p>
              <p className="text-[11px] text-slate-500 mt-1">
                Honorar: {a.hourlyRateEur.min}-{a.hourlyRateEur.max} EUR/h ·{" "}
                <span className={a.status === "verified" ? "text-emerald-400" : "text-amber-400"}>
                  {a.status === "verified" ? "verifiziert" : "MVP-Test-Berater"}
                </span>
              </p>
            </button>
          );
        })}
      </div>

      {chosen && (
        <div className="space-y-3 pt-2 border-t border-slate-800">
          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Anonymisierte Profil-Zusammenfassung *
            </label>
            <textarea
              value={profileSummary}
              onChange={(e) => setProfileSummary(e.target.value)}
              rows={4}
              placeholder="Was soll der Berater wissen? z.B. 'GmbH-Gesellschafter, 1.5M EUR Vermögen, Wegzug in EU-Staat in 18 Monaten geplant. Familie zieht mit. Ziel: Steuerlast reduzieren ohne § 42 AO Risiko.'"
              className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-court-500"
            />
            <p className="mt-1 text-xs text-slate-500">
              {profileSummary.trim().length}/20 Zeichen Minimum
              {profileSummary.trim().length >= 20 && " ✓"}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Dein Name *" value={contactName} onChange={setContactName} placeholder="Vor- und Nachname" />
            <Field
              label="Email *"
              value={contactEmail}
              onChange={setContactEmail}
              placeholder="name@example.com"
            />
            <Field
              label="Telefon (optional)"
              value={contactPhone}
              onChange={setContactPhone}
              placeholder="+49 …"
            />
          </div>

          <label className="flex items-start gap-2 text-xs text-slate-300">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Ich stimme zu, dass YAVU meine Kontakt- und Profil-Daten an den
              ausgewählten Berater weitergibt. YAVU ist KEIN Anwalt/Steuerberater
              und tritt nur als Vermittler auf. Mandatsverhältnis entsteht erst
              durch separate Vereinbarung mit dem Berater.
            </span>
          </label>

          {submitError && (
            <div className="rounded-lg bg-red-950/50 border border-red-800 px-3 py-2 text-xs text-red-200">
              {submitError}
            </div>
          )}

          <button
            onClick={submit}
            disabled={
              submitting ||
              !consent ||
              profileSummary.trim().length < 20 ||
              !contactName ||
              !contactEmail
            }
            className="w-full rounded-lg bg-court-600 hover:bg-court-500 disabled:opacity-40 px-4 py-2.5 text-sm font-semibold text-white transition"
          >
            {submitting ? "Sende Anfrage…" : "📧 Anfrage an Berater vermitteln"}
          </button>
        </div>
      )}
    </div>
  );
}
